import { useGT } from 'gt-react';
import type { LeadTimeOverrideRow } from 'src/api/rpc/hooks/procurement/useLeadTimeOverrides';
import {
  OverrideHistoryList,
  OverrideHistoryArchive,
  OverrideHistoryRow,
  OverrideRowActions,
  OverrideStatusBadges,
} from '../components/OverrideHistory';
import {
  overrideDeactivationLine,
  overrideMetaLine,
  replacedOverrideIds,
} from '../components/overrideRows';
import {
  daysTextClasses,
  isActionable,
  statusBadgeClasses,
  statusLabel,
  statusOf,
  type OverrideStatus,
} from './leadTimeOverrideRows';

// Every override ever set for one series, newest first, with its status: the
// row chain IS the history, so an edited row shows as "replaced" rather than
// changing in place. The row layout is shared with the policy override list;
// what belongs to the lead time is the pinned days as the headline and the
// six-status vocabulary. See docs/procurement/lead-time-overrides.md.

function LeadTimeHistoryRow({
  override: o,
  status,
  active,
  onEdit,
  onRequestRevoke,
}: {
  override: LeadTimeOverrideRow;
  status: OverrideStatus;
  active: LeadTimeOverrideRow | null;
  onEdit: (o: LeadTimeOverrideRow) => void;
  onRequestRevoke: (o: LeadTimeOverrideRow, scheduled: boolean) => void;
}) {
  const gt = useGT();
  return (
    <OverrideHistoryRow
      headline={
        <span className={daysTextClasses(status)}>
          {gt('{days}d', {
            context: 'lead time override days',
            days: o.leadTimeDays,
          })}
        </span>
      }
      status={
        <OverrideStatusBadges
          label={statusLabel(status, gt)}
          className={statusBadgeClasses(status)}
          filled={status === 'in_effect'}
          needsReview={o.needsReview}
        />
      }
      meta={overrideMetaLine(o, gt)}
      usage={
        status === 'scheduled'
          ? gt(
              'Eligible for calculations from {date}. Does not change today’s lead time; precedence is checked when the calculation runs.',
              {
                context: 'future lead time override eligibility explanation',
                date: new Date(o.startsAt).toLocaleDateString(),
              },
            )
          : status === 'shadowed' && active
            ? gt(
                'The {days}d total lead time override takes precedence today. Can apply if higher-priority total lead time overrides end or are revoked while this window remains eligible.',
                {
                  context: 'lead time override fallback explanation',
                  days: active.leadTimeDays,
                },
              )
            : null
      }
      deactivation={overrideDeactivationLine(o, status === 'replaced', gt)}
      actions={
        isActionable(status) ? (
          <OverrideRowActions
            override={o}
            scheduled={status === 'scheduled'}
            onEdit={onEdit}
            onRequestRevoke={onRequestRevoke}
          />
        ) : null
      }
    />
  );
}

export function LeadTimeOverrideHistory({
  overrides,
  active,
  onEdit,
  onRequestRevoke,
}: {
  /** Newest first, as the list endpoint returns them. */
  overrides: readonly LeadTimeOverrideRow[];
  active: LeadTimeOverrideRow | null;
  onEdit: (o: LeadTimeOverrideRow) => void;
  onRequestRevoke: (o: LeadTimeOverrideRow, scheduled: boolean) => void;
}) {
  if (overrides.length === 0) return null;
  const replacedIds = replacedOverrideIds(overrides);
  const now = new Date();
  const live = overrides.filter((o) =>
    isActionable(statusOf(o, active, replacedIds, now)),
  );
  const history = overrides.filter(
    (o) => !isActionable(statusOf(o, active, replacedIds, now)),
  );
  const renderRow = (o: LeadTimeOverrideRow) => (
    <LeadTimeHistoryRow
      key={o.id}
      override={o}
      status={statusOf(o, active, replacedIds, now)}
      active={active}
      onEdit={onEdit}
      onRequestRevoke={onRequestRevoke}
    />
  );
  return (
    <>
      {live.length > 0 ? (
        <OverrideHistoryList>{live.map(renderRow)}</OverrideHistoryList>
      ) : null}
      <OverrideHistoryArchive count={history.length}>
        {history.map(renderRow)}
      </OverrideHistoryArchive>
    </>
  );
}
